import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useAuth, useUser } from "@clerk/clerk-react";
import "./Pricing.css";
import { getPackages, getPricing } from "../../../services/api/app/credits";
import { getBenefits, getPlans, getStatus } from "../../../services/api/app/subscriptions";
import {
  confirmPayment,
  createCreditOrder,
  createSubscriptionCheckout,
  getPayment,
} from "../../../services/api/app/payments";
import { openHostedCheckout } from "../../../utils/payments/hostedCheckout";
import { publishCreditsBalanceChanged } from "../../../config/app/signupCredits";
import { syncUser } from "../../../services/api/app/auth";
import logger from "../../../utils/logger";
import {
  YEARLY_SAVE_PERCENT,
  billingModes,
  creditPacks,
  defaultTierCredits,
  applyToolCostsToFeatureGroups,
  applyPlanFamiliesToFeatureGroups,
  featureColumns,
  featureGroups,
  DEFAULT_STUDIO_ID,
  generationsFromCredits,
  getPlanTiers,
  getPricingCountry,
  detectPricingCountryId,
  countryIdForCurrency,
  catalogMajor,
  formatCurrencyAmount,
  isSubscription,
  perImageLabel,
  plans,
  resolvePlanTier,
  yearlyFromMonthly,
  comparePlanSelection,
  currentPlanFromBenefits,
} from "../../../data/pricingModal";

function formatCreditsShort(credits) {
  if (credits >= 1000) {
    const thousands = credits / 1000;
    return `${thousands}k`;
  }
  return String(credits);
}

const creditPackId = (pkg) =>
  pkg?.credit_pack_id ?? pkg?.id ?? pkg?.package_id ?? null;

const subscriptionPackId = (pkg) => pkg?.package_id ?? pkg?.id ?? null;

const packageCredits = (pkg) =>
  Number(pkg?.credits_allotted ?? pkg?.credits ?? pkg?.credits_per_month ?? 0);

function HintTip({ text, className, children }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(null);

  function hide() {
    setPos(null);
  }

  function show() {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const above = rect.top > 132;
    setPos({
      left: rect.left + rect.width / 2,
      top: above ? rect.top - 8 : rect.bottom + 8,
      above,
    });
  }

  useEffect(() => {
    if (!pos) return undefined;
    window.addEventListener("scroll", hide, true);
    window.addEventListener("resize", hide);
    return () => {
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("resize", hide);
    };
  }, [pos]);

  return (
    <>
      <span
        ref={ref}
        className={className}
        tabIndex={0}
        aria-label={text}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
      >
        {children}
      </span>
      {pos
        ? createPortal(
            <span
              className={`pricing-modal__hint-bubble${
                pos.above ? " is-above" : " is-below"
              }`}
              role="tooltip"
              style={{ left: pos.left, top: pos.top }}
            >
              {text}
            </span>,
            document.body
          )
        : null}
    </>
  );
}

function HintIcon({ text }) {
  return (
    <HintTip text={text} className="pricing-modal__hint">
      i
    </HintTip>
  );
}

function FeatureCell({ value, active, hint }) {
  if (value === true) {
    return (
      <span
        className={`pricing-modal__mark pricing-modal__mark--yes${
          active ? " is-active" : ""
        }`}
        aria-label="Included"
      >
        ✓
      </span>
    );
  }
  if (value === false) {
    return (
      <span
        className="pricing-modal__mark pricing-modal__mark--no"
        aria-label="Not included"
      >
        ✕
      </span>
    );
  }
  const className = `pricing-modal__cell-text${active ? " is-active" : ""}${
    hint ? " pricing-modal__hint-link" : ""
  }`;
  if (hint) {
    return (
      <HintTip text={hint} className={className}>
        {value}
      </HintTip>
    );
  }
  return <span className={className}>{value}</span>;
}

function PlanTierSlider({ planId, planName, tiers, credits, currentCredits, onChange }) {
  if (!tiers.length) return null;
  const index = Math.max(
    0,
    tiers.findIndex((tier) => tier.credits === credits)
  );
  const fill =
    tiers.length > 1 ? `${(index / (tiers.length - 1)) * 100}%` : "0%";

  return (
    <span
      className="pricing-modal__slider"
      style={{ "--slider-fill": fill }}
      onClick={(event) => event.stopPropagation()}
      onPointerDown={(event) => event.stopPropagation()}
    >
      <span className="pricing-modal__slider-control">
        <span className="pricing-modal__slider-track">
          <span className="pricing-modal__slider-fill" />
        </span>
        <input
          type="range"
          min={0}
          max={tiers.length - 1}
          step={1}
          value={index}
          onChange={(event) => {
            const next = tiers[Number(event.target.value)];
            if (next) onChange(planId, next.credits);
          }}
          aria-label={`${planName} credits per month`}
        />
      </span>
      <span className="pricing-modal__slider-scale">
        <span className="pricing-modal__slider-labels">
          {tiers.map((tier, tierIndex) => {
            const edge =
              tierIndex === 0
                ? " is-start"
                : tierIndex === tiers.length - 1
                  ? " is-end"
                  : "";
            const isCurrent =
              currentCredits != null && tier.credits === currentCredits;
            return (
              <button
                key={tier.credits}
                type="button"
                title={isCurrent ? "Current plan" : undefined}
                className={`pricing-modal__slider-caption${edge}${
                  tierIndex === index ? " is-active" : ""
                }${isCurrent ? " is-current" : ""}`}
                style={{
                  left:
                    tiers.length > 1
                      ? `${(tierIndex / (tiers.length - 1)) * 100}%`
                      : "0%",
                }}
                onClick={() => onChange(planId, tier.credits)}
              >
                {formatCreditsShort(tier.credits)}
              </button>
            );
          })}
        </span>
      </span>
    </span>
  );
}

const Pricing = ({
  onClose,
  onCreditsUpdated,
  bannerMessage = null,
}) => {
  const modalRef = useRef();
  const { getToken } = useAuth();
  const { user } = useUser();

  const defaultPack =
    creditPacks.find((pack) => pack.badge === "Popular") || creditPacks[1];
  const defaultPlan =
    plans.find((plan) => plan.badge === "Popular") || plans[1];

  const [billing, setBilling] = useState("monthly");
  const [selectedId, setSelectedId] = useState(defaultPlan.id);
  const [packId, setPackId] = useState(defaultPack.id);
  const [tierCredits, setTierCredits] = useState(defaultTierCredits);
  const [openStudio, setOpenStudio] = useState(DEFAULT_STUDIO_ID);
  const [countryId, setCountryId] = useState(() => detectPricingCountryId());

  const [apiCreditPackages, setApiCreditPackages] = useState([]);
  const [apiSubscriptionPlans, setApiSubscriptionPlans] = useState([]);
  const [pricingTools, setPricingTools] = useState([]);
  const [planFamilies, setPlanFamilies] = useState([]);
  const [currentPlan, setCurrentPlan] = useState(null);
  const pinnedCurrentRef = useRef(false);
  const [purchasing, setPurchasing] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  const [statusError, setStatusError] = useState(false);

  const country = getPricingCountry(countryId);
  const displayCurrency = country.currency === "INR" ? "INR" : "USD";
  const subscription = isSubscription(billing);
  const selectedPack =
    creditPacks.find((pack) => pack.id === packId) || defaultPack;
  const selectedPackOption = creditPacks.find((pack) => pack.id === selectedId);
  const selectedPlan = plans.find((plan) => plan.id === selectedId);
  const selectedTier = selectedPlan
    ? resolvePlanTier(selectedPlan.id, tierCredits[selectedPlan.id])
    : null;

  const selectedPrice = useMemo(() => {
    if (!subscription && selectedPackOption) {
      const apiPack = apiCreditPackages.find(
        (p) => packageCredits(p) === Number(selectedPackOption.credits),
      );
      return {
        amount: Number(apiPack?.price ?? apiPack?.credit_pack_price ?? selectedPackOption.price),
        credits: selectedPackOption.credits,
      };
    }
    if (selectedTier) {
      const family = selectedPlan?.id;
      const apiPlan = apiSubscriptionPlans.find(
        (p) =>
          packageCredits(p) === Number(selectedTier.credits) &&
          (!family || String(p.family || "").toLowerCase() === String(family).toLowerCase()),
      );
      return {
        amount: Number(apiPlan?.price ?? apiPlan?.package_price ?? selectedTier.monthly),
        credits: selectedTier.credits,
      };
    }
    return { amount: 0, credits: 0 };
  }, [
    subscription,
    selectedPackOption,
    selectedTier,
    selectedPlan,
    apiCreditPackages,
    apiSubscriptionPlans,
  ]);

  const tableFeatureGroups = useMemo(
    () =>
      applyPlanFamiliesToFeatureGroups(
        applyToolCostsToFeatureGroups(featureGroups, pricingTools),
        planFamilies,
      ),
    [pricingTools, planFamilies],
  );

  const headline = subscription
    ? billing === "yearly"
      ? `Pricing Plans`
      : "Pricing Plans"
    : "Buy credits";

  const selectionAction = useMemo(() => {
    if (!subscription) return "payg";
    if (billing === "yearly") return "yearly";
    return comparePlanSelection(currentPlan, {
      family: selectedPlan?.id,
      credits: selectedTier?.credits,
    });
  }, [subscription, billing, currentPlan, selectedPlan, selectedTier]);

  const ctaDisabled =
    purchasing ||
    (subscription &&
      billing !== "yearly" &&
      selectionAction !== "upgrade" &&
      selectionAction !== "downgrade" &&
      selectionAction !== "new");

  const ctaLabel = purchasing
    ? "Processing…"
    : !subscription
      ? `Buy ${selectedPrice.credits.toLocaleString()} credits`
      : billing === "yearly"
        ? `Upgrade with ${YEARLY_SAVE_PERCENT}% OFF`
        : selectionAction === "current"
          ? "Current plan"
          : selectionAction === "downgrade"
            ? `Downgrade to ${selectedPlan?.name ?? "Lite"}${
                selectedTier?.credits
                  ? ` ${selectedTier.credits.toLocaleString()}`
                  : ""
              } next cycle`
            : `Upgrade to ${selectedPlan?.name ?? "Lite"}${
                selectedTier?.credits
                  ? ` ${selectedTier.credits.toLocaleString()}`
                  : ""
              }`;

  const showStatus = (message, isError = false) => {
    setStatusMessage(message);
    setStatusError(isError);
  };

  const loadApiCatalog = useCallback(async () => {
    try {
      let credits = [];
      let subs = [];
      let tools = [];
      let families = [];
      try {
        const pricing = await getPricing();
        if (pricing?.success) {
          credits = pricing.packs || [];
          subs = pricing.plans || [];
          tools = pricing.tools || [];
          families = pricing.plan_families || [];
          if (pricing.display_currency === "USD" || pricing.display_currency === "INR") {
            setCountryId(countryIdForCurrency(pricing.display_currency));
          }
        }
      } catch (pricingErr) {
        logger.warn("GET /api/pricing unavailable, falling back to pack/plan endpoints", pricingErr);
      }
      if (!credits.length || !subs.length) {
        const [creditsRes, subsRes] = await Promise.all([
          getPackages(),
          getPlans(),
        ]);
        credits = credits.length ? credits : creditsRes?.packages || [];
        subs = subs.length ? subs : subsRes?.plans || [];
      }

      setApiCreditPackages(
        credits
          .map((p) => ({ ...p, id: creditPackId(p) }))
          .filter((p) => p.id != null)
          .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0)),
      );
      setApiSubscriptionPlans(
        subs
          .map((p) => ({ ...p, id: subscriptionPackId(p) }))
          .filter((p) => p.id != null)
          .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0)),
      );
      setPricingTools(tools);
      setPlanFamilies(families);
    } catch (err) {
      logger.error("Failed to load pricing API catalog", err);
    }
  }, []);

  useEffect(() => {
    loadApiCatalog();
  }, [loadApiCatalog]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken({ skipCache: true });
        if (!token || cancelled) return;
        let benefits = null;
        try {
          const status = await getStatus(token);
          benefits = status?.benefits;
        } catch (statusErr) {
          logger.warn("Could not load subscription status", statusErr);
        }
        if (!benefits?.has_subscription) {
          try {
            const res = await getBenefits(token);
            benefits = res?.benefits || benefits;
          } catch (benefitsErr) {
            logger.warn("Could not load subscription benefits", benefitsErr);
          }
        }
        if (cancelled) return;
        setCurrentPlan(currentPlanFromBenefits(benefits));
      } catch (err) {
        logger.warn("Could not load current subscription", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  useEffect(() => {
    if (!currentPlan?.family || pinnedCurrentRef.current) return;
    pinnedCurrentRef.current = true;
    setSelectedId(currentPlan.family);
    setTierCredits((prev) => ({
      ...prev,
      [currentPlan.family]: currentPlan.credits,
    }));
  }, [currentPlan]);

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKey = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  const handleOverlayClick = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      onClose();
    }
  };

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const waitUntilPaid = async (token, lzPaymentId) => {
    let latest = null;
    for (let i = 0; i < 8; i += 1) {
      latest = await getPayment(token, lzPaymentId);
      if (latest?.status === "paid" || latest?.status === "failed") return latest;
      await sleep(800);
    }
    return latest;
  };

  const runCheckout = async (startCheckout) => {
    setPurchasing(true);
    setStatusMessage(null);
    setStatusError(false);
    try {
      let token = await getToken();
      if (!token) {
        showStatus("Please sign in to purchase credits.", true);
        return;
      }

      try {
        await syncUser(token);
      } catch (syncErr) {
        logger.warn("Sync before purchase failed", syncErr);
      }

      const session = await startCheckout(token);
      if (!session?.success) {
        showStatus(session?.message || session?.detail || "Could not start checkout.", true);
        return;
      }

      if (session.scheduled_change === "cycle_end") {
        showStatus(
          session.description ||
            "Downgrade scheduled. Your current plan stays until the next billing date.",
          false,
        );
        if (typeof onCreditsUpdated === "function") {
          onCreditsUpdated();
        }
        return;
      }

      let result = session;
      if (session.status !== "paid") {
        showStatus("Complete payment in the checkout window…", false);
        const checkoutResult = await openHostedCheckout(session, user);
        // Clerk JWTs expire while the Razorpay modal is open; reuse would 401 confirm.
        token = (await getToken({ skipCache: true })) || token;
        if (!token) {
          showStatus("Session expired. Please sign in again. Payment was captured and credits will appear shortly.", true);
          return;
        }
        result = await confirmPayment(token, {
          lz_payment_id: session.lz_payment_id,
          order_id: checkoutResult.order_id,
          payment_id: checkoutResult.payment_id,
          subscription_id: checkoutResult.subscription_id,
          signature: checkoutResult.signature,
        });
        if (result?.status !== "paid" && session.lz_payment_id) {
          result = (await waitUntilPaid(token, session.lz_payment_id)) || result;
        }
      }

      if (result?.success && result?.status === "paid") {
        const added = result.credits_added ?? result.monthly_credits ?? result.total_credits;
        const msg =
          result.message ||
          (added != null
            ? `Success! ${added} credits added to your account.`
            : "Payment received. Credits will appear in your wallet shortly.");
        showStatus(msg, false);
        if (result.remaining_credits != null) {
          publishCreditsBalanceChanged(result.remaining_credits);
        }
        if (typeof onCreditsUpdated === "function") {
          onCreditsUpdated();
        }
      } else if (result?.status === "failed") {
        showStatus("Payment failed. No credits were added.", true);
      } else {
        showStatus(
          result?.message ||
            "Payment is still processing. Credits will appear after confirmation.",
          false,
        );
        if (typeof onCreditsUpdated === "function") {
          onCreditsUpdated();
        }
      }
    } catch (err) {
      if (err?.cancelled) {
        showStatus("Checkout closed. No payment was made.", true);
        return;
      }
      logger.error("Purchase failed", err);
      showStatus(err.message || "Purchase failed. Please try again.", true);
    } finally {
      setPurchasing(false);
    }
  };

  const handleCta = () => {
    if (!subscription) {
      const pack = selectedPackOption || selectedPack;
      const match = apiCreditPackages.find(
        (p) => packageCredits(p) === Number(pack.credits),
      );

      if (!match?.id) {
        showStatus("No matching credit package available yet.", true);
        return;
      }
      runCheckout((token) => createCreditOrder(token, match.id, displayCurrency));
      return;
    }

    if (billing !== "yearly") {
      if (selectionAction === "current") return;
    }

    const credits = selectedTier?.credits ?? selectedPlan?.credits ?? 0;
    const family = selectedPlan?.id;
    const match =
      apiSubscriptionPlans.find(
        (p) =>
          packageCredits(p) === Number(credits) &&
          (!family ||
            String(p.family || "").toLowerCase() === String(family).toLowerCase()),
      ) ||
      apiSubscriptionPlans.find(
        (p) => packageCredits(p) === Number(credits),
      );

    if (!match?.id) {
      showStatus("No matching subscription plan available yet.", true);
      return;
    }
    runCheckout((token) => createSubscriptionCheckout(token, match.id, displayCurrency));
  };

  function handleBilling(next) {
    setBilling(next);
    if (isSubscription(next)) {
      if (!plans.some((plan) => plan.id === selectedId)) {
        setSelectedId(defaultPlan.id);
      }
      return;
    }
    if (!creditPacks.some((pack) => pack.id === selectedId)) {
      setSelectedId(packId);
    }
  }

  function setPlanCredits(planId, credits) {
    setTierCredits((prev) => ({ ...prev, [planId]: credits }));
    setSelectedId(planId);
  }

  function planTierFor(plan) {
    return resolvePlanTier(plan.id, tierCredits[plan.id]);
  }

  function packDisplayMajor(pack) {
    const apiPack = apiCreditPackages.find(
      (p) => packageCredits(p) === Number(pack.credits),
    );
    const major = catalogMajor(apiPack, displayCurrency, pack.price);
    if (major == null) return catalogMajor(apiPack, "USD", pack.price);
    return major;
  }

  function packDisplayCurrency(pack) {
    const apiPack = apiCreditPackages.find(
      (p) => packageCredits(p) === Number(pack.credits),
    );
    const major = catalogMajor(apiPack, displayCurrency, pack.price);
    return major == null ? "USD" : displayCurrency;
  }

  function planDisplayMajor(plan, monthlyUsd) {
    const credits = planTierFor(plan).credits;
    const family = plan.id;
    const apiPlan = apiSubscriptionPlans.find(
      (p) =>
        packageCredits(p) === Number(credits) &&
        (!family ||
          String(p.family || "").toLowerCase() === String(family).toLowerCase()),
    );
    const major = catalogMajor(apiPlan, displayCurrency, monthlyUsd);
    if (major == null) return catalogMajor(apiPlan, "USD", monthlyUsd);
    return major;
  }

  function planDisplayCurrency(plan, monthlyUsd) {
    const credits = planTierFor(plan).credits;
    const family = plan.id;
    const apiPlan = apiSubscriptionPlans.find(
      (p) =>
        packageCredits(p) === Number(credits) &&
        (!family ||
          String(p.family || "").toLowerCase() === String(family).toLowerCase()),
    );
    const major = catalogMajor(apiPlan, displayCurrency, monthlyUsd);
    return major == null ? "USD" : displayCurrency;
  }

  function planPrice(plan) {
    const tier = planTierFor(plan);
    const { monthly, credits } = tier;
    const currency = planDisplayCurrency(plan, monthly);
    if (billing === "yearly") {
      const yearly = yearlyFromMonthly(planDisplayMajor(plan, monthly));
      return {
        primary: formatCurrencyAmount(yearly.perMonth, currency),
        suffix: "/m",
        yearly: formatCurrencyAmount(yearly.yearlyTotal, currency),
        original: formatCurrencyAmount(yearly.originalYearly, currency),
        perImage: perImageLabel(yearly.perMonth, credits, getPricingCountry(currency === "INR" ? "in" : "us")),
        badge: null,
      };
    }
    const major = planDisplayMajor(plan, monthly);
    return {
      primary: formatCurrencyAmount(major, currency),
      suffix: "/m",
      yearly: null,
      original: null,
      perImage: perImageLabel(major, credits, getPricingCountry(currency === "INR" ? "in" : "us")),
      badge: null,
    };
  }

  const activeColumnId = subscription ? selectedId : "onetime";

  function columnCredits(columnId) {
    if (columnId === "onetime") return selectedPack.credits;
    return resolvePlanTier(columnId, tierCredits[columnId]).credits;
  }

  function rowValue(row, columnId) {
    if (row.creditsPerUse) {
      const cost =
        typeof row.creditsPerUse === "object"
          ? Number(row.creditsPerUse[columnId])
          : Number(row.creditsPerUse);
      if (row.showCreditCost) {
        const available =
          columnId === "onetime" ? columnCredits(columnId) : null;
        if (available != null && available < cost) return "0";
        return `+${cost} Credits`;
      }
      return generationsFromCredits(columnCredits(columnId), cost);
    }

    if (columnId === "onetime") {
      if (row.label === "Ai Credits") {
        return `${selectedPack.credits.toLocaleString()} credits`;
      }
      if (row.label === "Exports") {
        return selectedPack.credits.toLocaleString();
      }
      if (row.label === "Per image") {
        return perImageLabel(
          packDisplayMajor(selectedPack),
          selectedPack.credits,
          getPricingCountry(packDisplayCurrency(selectedPack) === "INR" ? "in" : "us"),
        );
      }
      if (selectedPack.features && row.label in selectedPack.features) {
        return selectedPack.features[row.label];
      }
    }

    const raw = row.values?.[columnId];
    if (raw !== "slider") return raw;

    const tier = resolvePlanTier(columnId, tierCredits[columnId]);
    if (row.label === "Ai Credits") {
      return `${tier.credits.toLocaleString()}/m`;
    }
    if (row.label === "Exports") {
      return `${tier.credits.toLocaleString()}/m`;
    }
    if (row.label === "Per image") {
      const plan = plans.find((item) => item.id === columnId);
      const major =
        billing === "yearly"
          ? yearlyFromMonthly(planDisplayMajor(plan || { id: columnId }, tier.monthly)).perMonth
          : planDisplayMajor(plan || { id: columnId }, tier.monthly);
      const currency = planDisplayCurrency(plan || { id: columnId }, tier.monthly);
      return perImageLabel(
        major,
        tier.credits,
        getPricingCountry(currency === "INR" ? "in" : "us"),
      );
    }
    return raw;
  }

  function renderFeatureRow(groupKey, row) {
    return (
      <tr
        key={`${groupKey}-${row.label}`}
        className="pricing-modal__feature-row"
      >
        <th scope="row">
          {row.label}
          {row.hint ? <HintIcon text={row.hint} /> : null}
        </th>
        {featureColumns.map((column) => (
          <td
            key={column.id}
            className={activeColumnId === column.id ? "is-active" : undefined}
          >
            <FeatureCell
              value={rowValue(row, column.id)}
              active={activeColumnId === column.id}
              hint={row.valueHints?.[column.id]}
            />
          </td>
        ))}
      </tr>
    );
  }

  function renderSectionHeading(groupKey, section) {
    if (!section.label) return null;
    return (
      <tr
        key={`${groupKey}-${section.label}-heading`}
        className="pricing-modal__group-row pricing-modal__group-row--sub"
      >
        <th scope="colgroup" colSpan={featureColumns.length + 1}>
          <span className="pricing-modal__group-label">
            {section.label}
            {section.hint ? <HintIcon text={section.hint} /> : null}
          </span>
          {section.hint ? (
            <span className="pricing-modal__group-hint">{section.hint}</span>
          ) : null}
        </th>
      </tr>
    );
  }

  function renderGroupBody(group) {
    const sections =
      group.sections ?? [{ label: null, hint: group.hint, rows: group.rows }];
    return sections.flatMap((section) => [
      renderSectionHeading(group.id || group.label, section),
      ...(section.rows ?? []).map((row) =>
        renderFeatureRow(
          `${group.id || group.label}-${section.label || "rows"}`,
          row
        )
      ),
    ]);
  }

  return (
    <div className="pricing-overlay" onClick={handleOverlayClick}>
      <div
        className="pricing-stack"
        onClick={(event) => event.stopPropagation()}
      >
        {bannerMessage ? (
          <p className="pricing-modal__banner-note" role="status">
            {bannerMessage}
          </p>
        ) : null}
        <div
          ref={modalRef}
          className="pricing-container"
          role="dialog"
          aria-modal="true"
          aria-labelledby="pricing-modal-title"
        >
        <button
          type="button"
          className="pricing-close-btn"
          onClick={onClose}
          aria-label="Close pricing dialog"
        >
          ×
        </button>

        {statusMessage && (
          <p
            className={`pricing-status-message${
              statusError ? " pricing-status-message--error" : ""
            }`}
            role="status"
          >
            {statusMessage}
          </p>
        )}

        <div
          className="pricing-modal"
          data-accent={subscription ? selectedId : "onetime"}
        >
          <div className="pricing-modal__grid">
            <div className="pricing-modal__left">
              <div className="pricing-modal__head">
                <h2 id="pricing-modal-title" className="pricing-modal__title">
                  {headline}
                </h2>
                <span className="pricing-modal__currency" aria-label="Display currency">
                  <span className="pricing-modal__currency-code">{displayCurrency}</span>
                </span>
              </div>

              <div className="pricing-modal__toolbar">
                <div
                  className="pricing-modal__tabs"
                  role="tablist"
                  aria-label="Billing"
                >
                  {billingModes.map((mode) => (
                    <button
                      key={mode.id}
                      type="button"
                      role="tab"
                      aria-selected={billing === mode.id}
                      className={`pricing-modal__tab${
                        billing === mode.id ? " is-active" : ""
                      }`}
                      onClick={() => handleBilling(mode.id)}
                    >
                      {mode.label}
                      {mode.id === "yearly" && (
                        <span className="pricing-modal__save">
                          Save {YEARLY_SAVE_PERCENT}%
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              <div
                className="pricing-modal__cards"
                role="radiogroup"
                aria-label="Plan"
              >
                {!subscription &&
                  creditPacks.map((pack) => {
                    const active = selectedId === pack.id;
                    return (
                      <div
                        key={pack.id}
                        role="radio"
                        tabIndex={0}
                        aria-checked={active}
                        className={`pricing-modal__card pricing-modal__card--pack${
                          active ? " is-selected" : ""
                        }`}
                        onClick={() => {
                          setPackId(pack.id);
                          setSelectedId(pack.id);
                        }}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            setPackId(pack.id);
                            setSelectedId(pack.id);
                          }
                        }}
                      >
                        {pack.badge && (
                          <span className="pricing-modal__badge">
                            {pack.badge}
                          </span>
                        )}
                        <span className="pricing-modal__card-head">
                          <span
                            className={`pricing-modal__radio${
                              active ? " is-on" : ""
                            }`}
                            aria-hidden="true"
                          />
                          <span className="pricing-modal__plan-name">
                            <span className="pricing-modal__pack-count">
                              {pack.credits.toLocaleString()}
                            </span>
                            <span className="pricing-modal__pack-unit">
                              Credits
                            </span>
                          </span>
                          <span className="pricing-modal__price">
                            {formatCurrencyAmount(
                              packDisplayMajor(pack),
                              packDisplayCurrency(pack),
                            )}
                          </span>
                        </span>
                        <span className="pricing-modal__card-rest">
                          <span className="pricing-modal__per-row">
                            <span />
                            <span className="pricing-modal__per">
                              {perImageLabel(
                                packDisplayMajor(pack),
                                pack.credits,
                                getPricingCountry(
                                  packDisplayCurrency(pack) === "INR" ? "in" : "us",
                                ),
                              )}
                            </span>
                          </span>
                        </span>
                      </div>
                    );
                  })}

                {subscription &&
                  plans.map((plan) => {
                    const active = selectedId === plan.id;
                    const price = planPrice(plan);
                    const tier = planTierFor(plan);
                    const tiers = getPlanTiers(plan.id);
                    const cardAction = comparePlanSelection(currentPlan, {
                      family: plan.id,
                      credits: tier.credits,
                    });
                    const isCurrentFamily = currentPlan?.family === plan.id;
                    return (
                      <div
                        key={plan.id}
                        role="radio"
                        tabIndex={0}
                        aria-checked={active}
                        className={`pricing-modal__card pricing-modal__card--${plan.id}${
                          active ? " is-selected" : ""
                        }`}
                        onClick={() => setSelectedId(plan.id)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            setSelectedId(plan.id);
                          }
                        }}
                      >
                        {active && cardAction === "downgrade" ? (
                          <span className="pricing-modal__badge pricing-modal__badge--downgrade">
                            Downgrade
                          </span>
                        ) : isCurrentFamily ? (
                          <span className="pricing-modal__badge pricing-modal__badge--current">
                            Current
                          </span>
                        ) : (plan.badge || price.badge) ? (
                          <span className="pricing-modal__badge">
                            {plan.badge || price.badge}
                          </span>
                        ) : null}
                        <span className="pricing-modal__card-head">
                          <span
                            className={`pricing-modal__radio${
                              active ? " is-on" : ""
                            }`}
                            aria-hidden="true"
                          />
                          <span className="pricing-modal__plan-name">
                            <span className="pricing-modal__plan-title">
                              {plan.name}
                            </span>
                          </span>
                          <span className="pricing-modal__price-row">
                            <span className="pricing-modal__price">
                              {price.primary}
                              <span className="pricing-modal__price-suffix">
                                {price.suffix}
                              </span>
                            </span>
                          </span>
                        </span>
                        <span className="pricing-modal__card-rest">
                          <span className="pricing-modal__meta-row">
                            <span className="pricing-modal__plan-credits">
                              <span className="pricing-modal__pack-count">
                                {tier.credits.toLocaleString()}
                              </span>
                              <span className="pricing-modal__pack-unit">
                                Credits/m
                              </span>
                            </span>
                            {price.yearly ? (
                              <span className="pricing-modal__yearly">
                                {price.yearly}/year{" "}
                                <s>{price.original}/year</s>
                              </span>
                            ) : null}
                            <span className="pricing-modal__per">
                              {price.perImage}
                            </span>
                          </span>
                          {tiers.length > 1 && (
                            <PlanTierSlider
                              planId={plan.id}
                              planName={plan.name}
                              tiers={tiers}
                              credits={tier.credits}
                              currentCredits={
                                currentPlan?.family === plan.id
                                  ? currentPlan.credits
                                  : null
                              }
                              onChange={setPlanCredits}
                            />
                          )}
                        </span>
                      </div>
                    );
                  })}
              </div>

              <div className="pricing-modal__cta-wrap">
                <button
                  type="button"
                  className="pricing-modal__cta"
                  disabled={ctaDisabled}
                  onClick={handleCta}
                >
                  {ctaLabel}
                </button>
                {subscription ? (
                  <p className="pricing-modal__cta-note">
                    {selectionAction === "downgrade"
                      ? "You keep this plan until the cycle ends. The lower plan starts on the next billing date."
                      : selectionAction === "current"
                        ? "This is your current plan. Pick a lower family or slider amount to downgrade from next cycle."
                        : "Downgrade, upgrade, or cancel anytime."}
                  </p>
                ) : null}
              </div>
            </div>

            <div className="pricing-modal__right">
              <div className="pricing-modal__table-wrap">
                <table
                  className={`pricing-modal__table${
                    billing === "yearly" ? " has-col-badges" : ""
                  }`}
                >
                  <thead>
                    <tr>
                      <th>Features</th>
                      {featureColumns.map((column) => {
                        const showOff =
                          billing === "yearly" && column.id !== "onetime";
                        return (
                          <th
                            key={column.id}
                            className={
                              [
                                activeColumnId === column.id ? "is-active" : "",
                                showOff ? "has-badge" : "",
                              ]
                                .filter(Boolean)
                                .join(" ") || undefined
                            }
                          >
                            
                            {activeColumnId === column.id ? (
                              <span
                                className="pricing-modal__col-icon"
                                aria-hidden="true"
                              >
                                ★
                              </span>
                            ) : null}
                            {column.label}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {tableFeatureGroups.map((group) => {
                      if (group.kind === "studio") {
                        const open = openStudio === group.id;
                        return [
                          <tr
                            key={`${group.id}-studio`}
                            className={`pricing-modal__studio-row${
                              open ? " is-open" : ""
                            }`}
                          >
                            <th scope="row">
                              <button
                                type="button"
                                className="pricing-modal__studio-toggle"
                                aria-expanded={open}
                                onClick={() => setOpenStudio(group.id)}
                              >
                                <span
                                  className="pricing-modal__studio-caret"
                                  aria-hidden="true"
                                />
                                {group.label}
                              </button>
                            </th>
                            {featureColumns.map((column) => (
                              <td
                                key={column.id}
                                className={
                                  activeColumnId === column.id
                                    ? "is-active"
                                    : undefined
                                }
                              >
                                {!open && group.summary ? (
                                  <FeatureCell
                                    value={group.summary[column.id]}
                                    active={activeColumnId === column.id}
                                  />
                                ) : null}
                              </td>
                            ))}
                          </tr>,
                          ...(open ? renderGroupBody(group) : []),
                        ];
                      }

                      return [
                        <tr
                          key={`${group.id}-heading`}
                          className="pricing-modal__group-row"
                        >
                          <th
                            scope="colgroup"
                            colSpan={featureColumns.length + 1}
                          >
                            <span className="pricing-modal__group-label">
                              {group.label}
                              {group.hint ? (
                                <HintIcon text={group.hint} />
                              ) : null}
                            </span>
                            {group.hint ? (
                              <span className="pricing-modal__group-hint">
                                {group.hint}
                              </span>
                            ) : null}
                          </th>
                        </tr>,
                        ...renderGroupBody(group),
                      ];
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <p className="pricing-modal__footer-note">
          1 credit ≈ 1 image. Prices exclude applicable taxes.{" "}
          {subscription
            ? "Subscriptions renew until cancelled."
            : "Credit packs never expire."}
        </p>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
